import React from 'react';
import { Box, Typography, CircularProgress } from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import type { GalleryItem } from '../types';
import { useAnalyzePetReactions } from '@/hooks/parsnip/useParsnipAnalysis';

interface Props {
  item: GalleryItem;
  onClose: () => void;
  userEmail: string;
  isAdmin?: boolean;
  onRerunSuccess?: () => Promise<void> | void;
  onDelete?: (id: string) => Promise<void>;
}

// ─── Sub-components ───────────────────────────────────────────────────────────

const BulletPointItem: React.FC<{ point: string }> = ({ point }) => {
  const isIntro = point.trim().endsWith(':') && point.length < 60;
  if (isIntro) {
    return (
      <Typography sx={{ fontFamily: 'Maven Pro', fontSize: '13px', color: '#999', fontStyle: 'italic', px: 1, mb: 0.5 }}>
        {point}
      </Typography>
    );
  }
  const parts = point.split(/(\*\*[^*]+\*\*)/g);
  return (
    <Box
      sx={{
        display: 'flex', gap: 1.5, alignItems: 'flex-start',
        backgroundColor: '#fafafa', borderRadius: '12px',
        padding: '12px 14px', border: '1px solid #f0f0f0',
        transition: 'background 0.15s',
        '&:hover': { backgroundColor: '#f4f0ff' }, cursor: 'pointer',
      }}
    >
      <Box sx={{ mt: '6px', flexShrink: 0, width: 8, height: 8, borderRadius: '50%', background: 'linear-gradient(135deg, #F39A81, #959BEA)' }} />
      <Typography sx={{ fontFamily: 'Maven Pro', fontSize: '14px', lineHeight: 1.75, color: '#333' }}>
        {parts.map((part) =>
          part.startsWith('**') && part.endsWith('**')
            ? <strong key={part} style={{ color: '#111', fontWeight: 700 }}>{part.slice(2, -2)}</strong>
            : part
        )}
      </Typography>
    </Box>
  );
};

interface RerunButtonProps {
  isRerunning: boolean;
  onClick: () => void;
}

const RerunButton: React.FC<RerunButtonProps> = ({ isRerunning, onClick }) => (
  <Box
    onClick={onClick}
    sx={{
      display: 'flex', alignItems: 'center', gap: 1,
      cursor: isRerunning ? 'not-allowed' : 'pointer',
      px: 2.5, py: 1.25, borderRadius: '24px',
      border: '2px solid transparent',
      backgroundImage: 'linear-gradient(white, white), linear-gradient(to right, #F39A81, #959BEA)',
      backgroundOrigin: 'border-box', backgroundClip: 'padding-box, border-box',
      transition: 'all 0.2s', opacity: isRerunning ? 0.6 : 1,
      '&:hover': isRerunning ? {} : { transform: 'scale(1.02)', boxShadow: '0 4px 12px rgba(243, 154, 129, 0.3)' },
    }}
  >
    {isRerunning ? (
      <CircularProgress size={16} sx={{ color: '#959BEA' }} />
    ) : (
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M17.65 6.35C16.2 4.9 14.21 4 12 4c-4.42 0-7.99 3.58-7.99 8s3.57 8 7.99 8c3.73 0 6.84-2.55 7.73-6h-2.08c-.82 2.33-3.04 4-5.65 4-3.31 0-6-2.69-6-6s2.69-6 6-6c1.66 0 3.14.69 4.22 1.78L13 11h7V4l-2.35 2.35z" fill="url(#rerun-gradient)" />
        <defs>
          <linearGradient id="rerun-gradient" x1="0" y1="0" x2="24" y2="24" gradientUnits="userSpaceOnUse">
            <stop offset="0%" stopColor="#F39A81" />
            <stop offset="100%" stopColor="#959BEA" />
          </linearGradient>
        </defs>
      </svg>
    )}
    <Typography
      sx={{
        fontFamily: 'Maven Pro', fontSize: '13px', fontWeight: 600,
        background: 'linear-gradient(to right, #F39A81, #959BEA)',
        WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
      }}
    >
      {isRerunning ? 'Starting Analysis...' : 'Re-run Analysis'}
    </Typography>
  </Box>
);

// ─── Main component ───────────────────────────────────────────────────────────

const AnalysisDetailModal: React.FC<Props> = ({ item, onClose, userEmail, isAdmin, onRerunSuccess, onDelete }) => {
  const [isDeleting, setIsDeleting] = React.useState(false);
  const allVideos = [
    ...(item.content_video_url ? [{ url: item.content_video_url, label: 'Content' }] : []),
    ...item.result_video_urls.map((url, i) => ({ url, label: `Reaction ${i + 1}` })),
  ];
  const [activeUrl, setActiveUrl] = React.useState(allVideos[0]?.url ?? '');
  const activeLabel = allVideos.find((v) => v.url === activeUrl)?.label ?? '';
  const [copied, setCopied] = React.useState(false);
  const [isRerunning, setIsRerunning] = React.useState(false);
  const [rerunMessage, setRerunMessage] = React.useState<{ type: 'success' | 'error', text: string } | null>(null);
  const [hasRecentRerun, setHasRecentRerun] = React.useState(false);
  const analyzeMutation = useAnalyzePetReactions();

  const isOwner = !item.user_email || item.user_email === userEmail;

  React.useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = ''; };
  }, []);

  React.useEffect(() => {
    const storageKey = `parsnip-rerun-${item.id}`;
    const storedTimestamp = localStorage.getItem(storageKey);
    if (!storedTimestamp) return;

    const timestamp = Number.parseInt(storedTimestamp, 10);
    if (timestamp > Date.now() - 10 * 60 * 1000) {
      setHasRecentRerun(true);
      setRerunMessage({ type: 'success', text: 'Analysis started. It will update automatically when complete.' });
    } else {
      localStorage.removeItem(storageKey);
    }
  }, [item.id]);

  const handleCopy = () => {
    const parts: string[] = [];
    if (item.summary) parts.push(`SUMMARY:\n${item.summary}`);
    if (item.bullet_points.length > 0) {
      const observations = item.bullet_points
        .map((p) => p.split('**').join(''))
        .join('\n');
      parts.push(`DETAILED OBSERVATIONS:\n${observations}`);
    }
    if (item.content_reactions?.length > 0) {
      const reactions = item.content_reactions
        .map((p) => p.split('**').join(''))
        .join('\n');
      parts.push(`CONTENT REACTIONS:\n${reactions}`);
    }
    if (item.content_to_remove?.length > 0) {
      const removals = item.content_to_remove
        .map((p) => p.split('**').join(''))
        .join('\n');
      parts.push(`CONTENT TO REMOVE:\n${removals}`);
    }
    if (item.content_suggestions?.length > 0) {
      const suggestions = item.content_suggestions
        .map((p) => p.split('**').join(''))
        .join('\n');
      parts.push(`CONTENT SUGGESTIONS:\n${suggestions}`);
    }
    navigator.clipboard.writeText(parts.join('\n\n')).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  const handleRerunAnalysis = async () => {
    if (!item.content_video_id || item.result_video_ids.length === 0) {
      setRerunMessage({ type: 'error', text: 'Missing video data for analysis' });
      setTimeout(() => setRerunMessage(null), 3000);
      return;
    }
    setIsRerunning(true);
    setRerunMessage(null);
    try {
      const response = await analyzeMutation.mutateAsync({
        analysis_id: item.id,
        content_video_id: item.content_video_id,
        result_video_ids: item.result_video_ids,
        pet_name: item.pet_name || undefined,
        pet_id: item.pet_id || undefined,
      });
      if (response.success) {
        localStorage.setItem(`parsnip-rerun-${item.id}`, Date.now().toString());
        setHasRecentRerun(true);
        if (response.status === 'completed') {
          setRerunMessage({ type: 'success', text: 'Analysis complete!' });
          localStorage.removeItem(`parsnip-rerun-${item.id}`);
          setHasRecentRerun(false);
        } else {
          setRerunMessage({ type: 'success', text: 'Analysis started. It will update automatically when complete.' });
        }
        await onRerunSuccess?.();
      } else {
        setRerunMessage({ type: 'error', text: 'Failed to start analysis. Please try again.' });
        setTimeout(() => setRerunMessage(null), 3000);
      }
    } catch (error) {
      console.error('[AnalysisDetailModal] Failed to rerun analysis:', error);
      setRerunMessage({ type: 'error', text: 'Failed to start analysis. Please try again.' });
      setTimeout(() => setRerunMessage(null), 3000);
    } finally {
      setIsRerunning(false);
    }
  };

  const [activeAnalysisTab, setActiveAnalysisTab] = React.useState<'observations' | 'reactions' | 'remove' | 'suggestions'>('observations');

  const hasAnalysis = item.bullet_points.length > 0 || !!item.summary || (item.content_reactions?.length ?? 0) > 0;

  return (
    <Box
      onClick={onClose}
      sx={{
        position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
        backgroundColor: 'rgba(0,0,0,0.7)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        zIndex: 9998, padding: 2,
      }}
    >
      <Box
        onClick={(e) => e.stopPropagation()}
        sx={{
          backgroundColor: 'white', borderRadius: '28px',
          width: '100%', maxWidth: '766px', maxHeight: '92vh',
          overflowY: 'auto', display: 'flex', flexDirection: 'column',
          boxShadow: '0 32px 80px rgba(0,0,0,0.4)',
        }}
      >
        {/* Header */}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '20px 24px 12px' }}>
          <Box>
            <Typography
              sx={{
                fontFamily: 'MuseoModerno', fontWeight: 600, fontSize: '20px',
                background: 'linear-gradient(to right, #F39A81, #959BEA)',
                WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
              }}
            >
              {item.pet_name ? `${item.pet_name}'s Analysis` : 'Analysis Results'}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mt: 0.25 }}>
              <Typography sx={{ fontFamily: 'Maven Pro', fontSize: '13px', color: '#aaa' }}>
                {activeLabel}
              </Typography>
              <Box sx={{ width: '1px', height: '14px', backgroundColor: '#ddd' }} />
              <Typography sx={{ fontFamily: 'Maven Pro', fontSize: '12px', color: '#999' }}>
                {new Date(item.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
              </Typography>
            </Box>
          </Box>
          <Box
            onClick={onClose}
            sx={{
              width: 36, height: 36, borderRadius: '50%', flexShrink: 0,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              cursor: 'pointer', backgroundColor: '#f5f5f5',
              '&:hover': { backgroundColor: '#e0e0e0' }, transition: 'background 0.2s',
            }}
          >
            <Typography sx={{ fontSize: '20px', lineHeight: 1, color: '#555' }}>×</Typography>
          </Box>
        </Box>

        {/* Main video player */}
        <Box sx={{ backgroundColor: '#000', mx: 2, borderRadius: '16px', overflow: 'hidden', flexShrink: 0 }}>
          <video
            key={activeUrl}
            src={activeUrl}
            controls
            autoPlay
            style={{ width: '100%', maxHeight: '420px', objectFit: 'contain', display: 'block' }}
          >
            <track kind="captions" />
          </video>
        </Box>

        {/* Video tiles row */}
        <Box
          sx={{
            display: 'flex', gap: 1.5, padding: '14px 24px',
            overflowX: 'auto', flexShrink: 0,
            '&::-webkit-scrollbar': { height: '4px' },
            '&::-webkit-scrollbar-thumb': { backgroundColor: '#ddd', borderRadius: '2px' },
          }}
        >
          {allVideos.map(({ url, label }) => (
            <Box
              key={url}
              onClick={() => setActiveUrl(url)}
              sx={{
                flexShrink: 0, width: '160px', cursor: 'pointer',
                borderRadius: '12px', overflow: 'hidden', backgroundColor: '#000',
                border: activeUrl === url ? '2px solid #959BEA' : '2px solid transparent',
                filter: activeUrl === url ? 'brightness(0.5) grayscale(0.4)' : 'none',
                transition: 'border-color 0.2s, transform 0.15s, filter 0.2s',
                '&:hover': { transform: 'scale(1.03)' },
                position: 'relative',
              }}
            >
              <video
                src={url}
                muted
                style={{ width: '100%', height: '90px', objectFit: 'cover', display: 'block', pointerEvents: 'none' }}
              >
                <track kind="captions" />
              </video>
              <Box
                sx={{
                  position: 'absolute', bottom: 0, left: 0, right: 0,
                  background: 'linear-gradient(to top, rgba(0,0,0,0.75) 0%, transparent 100%)',
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  padding: '4px 8px 5px',
                }}
              >
                <Typography sx={{ color: 'white', fontSize: '11px', fontFamily: 'Maven Pro', fontWeight: 600 }}>
                  {label}
                </Typography>
                <Typography sx={{ color: 'white', fontSize: '13px', lineHeight: 1 }}>▶</Typography>
              </Box>
            </Box>
          ))}
        </Box>

        {/* Analysis Results */}
        <Box sx={{ mx: 2, mb: 2 }}>
          {hasAnalysis ? (
            <>
              {item.summary && (
                <Box sx={{ mb: 3 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5, px: 1 }}>
                    <Box sx={{ flex: 1, height: '1.5px', background: 'linear-gradient(to right, #F39A81, #959BEA)' }} />
                    <Typography sx={{ fontFamily: 'MuseoModerno', fontWeight: 700, fontSize: '13px', background: 'linear-gradient(to left, #F39A81, #959BEA)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', whiteSpace: 'nowrap', letterSpacing: '0.06em', textTransform: 'uppercase' }}>
                      Overview
                    </Typography>
                    <Box sx={{ flex: 1, height: '1.5px', background: 'linear-gradient(to left, #F39A81, #959BEA)' }} />
                  </Box>
                  <Box sx={{ backgroundColor: '#fafafa', borderRadius: '12px', padding: '16px 18px', border: '1px solid #f0f0f0' }}>
                    <Typography sx={{ fontFamily: 'Maven Pro', fontSize: '14px', lineHeight: 1.8, color: '#333' }}>
                      {item.summary}
                    </Typography>
                  </Box>
                </Box>
              )}
              {item.bullet_points.length > 0 && (
                <>
                  {/* Tab bar */}
                  <Box
                    sx={{
                      display: 'flex', gap: 0.5, mb: 2,
                      overflowX: 'auto', flexShrink: 0,
                      '&::-webkit-scrollbar': { height: '3px' },
                      '&::-webkit-scrollbar-thumb': { backgroundColor: '#ddd', borderRadius: '2px' },
                    }}
                  >
                    {([
                      { key: 'observations' as const, label: 'Observations', count: item.bullet_points.length },
                      { key: 'reactions' as const, label: 'Reactions', count: item.content_reactions?.length ?? 0 },
                      { key: 'remove' as const, label: 'To Avoid', count: item.content_to_remove?.length ?? 0 },
                      { key: 'suggestions' as const, label: 'Suggestions', count: item.content_suggestions?.length ?? 0 },
                    ] as const).filter(t => t.count > 0).map((tab) => {
                      const isActive = activeAnalysisTab === tab.key;
                      return (
                        <Box
                          key={tab.key}
                          onClick={() => setActiveAnalysisTab(tab.key)}
                          sx={{
                            px: 2, py: 1, borderRadius: '20px', cursor: 'pointer',
                            flexShrink: 0, transition: 'all 0.2s',
                            display: 'flex', alignItems: 'center', gap: 0.75,
                            ...(isActive
                              ? {
                                  background: 'linear-gradient(135deg, #F39A81, #959BEA)',
                                  boxShadow: '0 2px 8px rgba(149, 155, 234, 0.3)',
                                }
                              : {
                                  backgroundColor: '#f5f5f5',
                                  '&:hover': { backgroundColor: '#ece8f5' },
                                }),
                          }}
                        >
                          <Typography
                            sx={{
                              fontFamily: 'Maven Pro', fontSize: '12px', fontWeight: 600,
                              whiteSpace: 'nowrap',
                              color: isActive ? '#fff' : '#666',
                            }}
                          >
                            {tab.label}
                          </Typography>
                          <Box
                            sx={{
                              minWidth: 18, height: 18, borderRadius: '9px',
                              display: 'flex', alignItems: 'center', justifyContent: 'center',
                              backgroundColor: isActive ? 'rgba(255,255,255,0.3)' : '#e0e0e0',
                            }}
                          >
                            <Typography sx={{ fontFamily: 'Maven Pro', fontSize: '10px', fontWeight: 700, color: isActive ? '#fff' : '#888', lineHeight: 1 }}>
                              {tab.count}
                            </Typography>
                          </Box>
                        </Box>
                      );
                    })}
                  </Box>

                  {/* Tab content */}
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
                    {activeAnalysisTab === 'observations' && item.bullet_points.map((point) => (
                      <BulletPointItem key={point.substring(0, 40)} point={point} />
                    ))}
                    {activeAnalysisTab === 'reactions' && item.content_reactions?.map((point) => (
                      <BulletPointItem key={point.substring(0, 40)} point={point} />
                    ))}
                    {activeAnalysisTab === 'remove' && item.content_to_remove?.map((point) => (
                      <BulletPointItem key={point.substring(0, 40)} point={point} />
                    ))}
                    {activeAnalysisTab === 'suggestions' && item.content_suggestions?.map((point) => (
                      <BulletPointItem key={point.substring(0, 40)} point={point} />
                    ))}
                  </Box>
                </>
              )}
            </>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2, py: 3 }}>
              <Typography sx={{ fontFamily: 'Maven Pro', color: '#999', fontStyle: 'italic', fontSize: '14px', textAlign: 'center' }}>
                No analysis data available.
              </Typography>
              {rerunMessage && (
                <Box
                  sx={{
                    px: 3, py: 2, borderRadius: '12px', maxWidth: '400px', width: '100%',
                    backgroundColor: rerunMessage.type === 'success' ? 'rgba(76, 175, 80, 0.1)' : 'rgba(244, 67, 54, 0.1)',
                    border: `1px solid ${rerunMessage.type === 'success' ? '#4caf50' : '#f44336'}`,
                  }}
                >
                  <Typography sx={{ fontFamily: 'Maven Pro', fontSize: '13px', fontWeight: 500, textAlign: 'center', color: rerunMessage.type === 'success' ? '#4caf50' : '#f44336' }}>
                    {rerunMessage.text}
                  </Typography>
                </Box>
              )}
              {isOwner && !hasRecentRerun && (
                <RerunButton isRerunning={isRerunning} onClick={handleRerunAnalysis} />
              )}
            </Box>
          )}

          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 2 }}>
            <Typography sx={{ fontFamily: 'Maven Pro', fontSize: '11px', color: '#666', fontWeight: 500 }}>
              Processed by {item.user_email || userEmail}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {hasAnalysis && (
                <Box
                  onClick={handleCopy}
                  sx={{
                    display: 'flex', alignItems: 'center', gap: 0.75, cursor: 'pointer',
                    px: 1.5, py: 0.75, borderRadius: '20px',
                    border: '2px solid transparent',
                    backgroundImage: 'linear-gradient(white, white), linear-gradient(to right, #F39A81, #959BEA)',
                    backgroundOrigin: 'border-box', backgroundClip: 'padding-box, border-box',
                    transition: 'all 0.2s',
                    '&:hover': { boxShadow: '0 2px 8px rgba(243, 154, 129, 0.3)' },
                  }}
                >
                  <Typography sx={{
                    fontFamily: 'Maven Pro', fontSize: '12px', fontWeight: 600,
                    background: 'linear-gradient(to right, #F39A81, #959BEA)',
                    WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
                  }}>
                    {copied ? '✓ Copied!' : 'Copy analysis'}
                  </Typography>
                </Box>
              )}
              {onDelete && isAdmin && (
                <Box
                  onClick={async () => {
                    if (isDeleting) return;
                    setIsDeleting(true);
                    try {
                      await onDelete(item.id);
                      onClose();
                    } finally {
                      setIsDeleting(false);
                    }
                  }}
                  sx={{
                    display: 'flex', alignItems: 'center', gap: 0.75, cursor: isDeleting ? 'not-allowed' : 'pointer',
                    px: 1.5, py: 0.75, borderRadius: '20px',
                    border: '1px solid #f44336',
                    opacity: isDeleting ? 0.6 : 1,
                    transition: 'all 0.2s',
                    '&:hover': { backgroundColor: 'rgba(244,67,54,0.06)' },
                  }}
                >
                  {isDeleting
                    ? <CircularProgress size={14} sx={{ color: '#f44336' }} />
                    : <DeleteIcon sx={{ fontSize: 14, color: '#f44336' }} />
                  }
                  <Typography sx={{ fontFamily: 'Maven Pro', fontSize: '12px', fontWeight: 600, color: '#f44336' }}>
                    {isDeleting ? 'Deleting...' : 'Delete'}
                  </Typography>
                </Box>
              )}
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default AnalysisDetailModal;
